import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "@services/api";
import { useAuth } from "@context/AuthContext";

const MARKETPLACE_KEY = "ww_active_marketplace";

const MarketplaceContext = createContext({
  isSuperAdmin: false,
  marketplaces: [],
  activeMarketplaceId: null,
  setActiveMarketplace: () => {},
  loading: false,
});

function readStored() {
  try {
    const val = localStorage.getItem(MARKETPLACE_KEY);
    return val === "all" ? null : val;
  } catch {
    return null;
  }
}

export function MarketplaceProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();

  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isMarketplaceAdmin, setIsMarketplaceAdmin] = useState(false);
  const [marketplaces, setMarketplaces] = useState([]);
  const [activeMarketplaceId, setActiveMarketplaceId] = useState(readStored());
  const [loading, setLoading] = useState(false);

  // Load the marketplaces this user may switch between.
  useEffect(() => {
    if (!isAuthenticated) {
      setIsSuperAdmin(false);
      setIsMarketplaceAdmin(false);
      setMarketplaces([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    api
      .get("/marketplaces")
      .then(({ data }) => {
        if (cancelled) return;
        
        const superAdmin = !!data.is_super_admin;
        const marketAdmin = !!data.is_marketplace_admin;
        
        setIsSuperAdmin(superAdmin);
        setIsMarketplaceAdmin(marketAdmin);
        
        const list = Array.isArray(data.marketplaces) ? data.marketplaces : [];
        setMarketplaces(list);

        // Sync the active selection with what the server resolved. The server
        // reports "all" (string) when nothing is scoped yet — normalize that
        // to null like readStored() does, so a marketplace admin with no
        // saved preference falls through to the "default to first
        // marketplace" branch below instead of the literal string "all"
        // getting persisted to localStorage and sent as the X-Marketplace-Id
        // header forever after, which is what was showing every other
        // marketplace's stores (Flash Wash, MSF Hospital, ...) in the Store
        // list on a fresh login. (WN-0154)
        const serverActive =
          data.active_marketplace_id === null ||
          data.active_marketplace_id === undefined ||
          data.active_marketplace_id === "all"
            ? null
            : String(data.active_marketplace_id);

        const stored = readStored();
        
        // If we have a stored selection and we are an admin (super or market), keep it.
        let next = ((superAdmin || marketAdmin) && stored) ? stored : serverActive;
        
        if (!next && list.length > 0) {
          next = String(list[0].franchise_id);
        }

        setActiveMarketplaceId(next);
        try {
          if (next) {
            localStorage.setItem(MARKETPLACE_KEY, next);
          } else {
            localStorage.removeItem(MARKETPLACE_KEY);
          }
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        /* leave defaults; switcher just won't show */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user]);

  const setActiveMarketplace = useCallback(
    (id) => {
      const value = id === null || id === undefined ? null : String(id);
      setActiveMarketplaceId(value);
      try {
        if (value) {
          localStorage.setItem(MARKETPLACE_KEY, value);
        } else {
          localStorage.removeItem(MARKETPLACE_KEY);
        }
      } catch {
        /* ignore */
      }
      // Refetch every cached query so all data reloads scoped to the new
      // marketplace (the api interceptor reads the header from localStorage).
      queryClient.invalidateQueries();
    },
    [queryClient]
  );

  const value = {
    isSuperAdmin,
    isMarketplaceAdmin,
    marketplaces,
    activeMarketplaceId,
    setActiveMarketplace,
    loading,
  };

  return (
    <MarketplaceContext.Provider value={value}>
      {children}
    </MarketplaceContext.Provider>
  );
}

export function useMarketplace() {
  return useContext(MarketplaceContext);
}
